import React, { useId, useState } from 'react';
import { Database, Plus } from 'lucide-react';
import { Icon, Text } from '@cotera/client/app/components/ui';
import { Button } from '@cotera/client/v0/components/ui/button';
import { Input } from '@cotera/client/v0/components/ui/input';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@cotera/client/v0/components/ui/select';
import { cn } from '@cotera/client/v0/lib/utils';
import {
  DATASET_COLUMN_TYPE_OPTIONS,
  type DatasetOperationColumnType,
} from './dataset-columns';
import { useDatasetTheme } from './dataset-theme';

/**
 * Picks a CSV off disk. Kept separate from what happens to the file so the same
 * control can seed an empty dataset or replace a populated one.
 */
export const DatasetImportCsvButton: React.FC<{
  label: string;
  variant?: 'ghost' | 'outline';
  onImportCsv: (file: File) => Promise<void>;
}> = ({ label, variant = 'ghost', onImportCsv }) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);

  return (
    <>
      <input
        ref={inputRef}
        type="file"
        accept=".csv,text/csv"
        className="hidden"
        onChange={(event) => {
          const file = event.target.files?.[0];
          event.target.value = '';
          if (file === undefined) {
            return;
          }
          setBusy(true);
          void onImportCsv(file)
            .catch(() => undefined)
            .finally(() => setBusy(false));
        }}
      />
      <Button
        type="button"
        size="sm"
        variant={variant}
        className="h-7"
        disabled={busy}
        onClick={() => inputRef.current?.click()}
      >
        <Icon icon="arrow-up-tray" size="small" />
        {busy ? 'Importing…' : label}
      </Button>
    </>
  );
};

const CSV_ACCEPT = '.csv,text/csv';

/**
 * Whether this is a CSV, by what the browser says or what the name ends in.
 *
 * Both, because neither is reliable alone: some browsers hand back
 * `application/vnd.ms-excel` for a `.csv` dragged off a machine with Excel
 * installed, and a file dropped from an archive can arrive with no type at all.
 */
const isCsvFile = (file: File): boolean =>
  file.type === 'text/csv' ||
  file.type === 'application/csv' ||
  file.name.toLowerCase().endsWith('.csv');

/**
 * Picks a CSV by drop or by browse.
 *
 * Separate from what happens to the file, like {@link DatasetImportCsvButton},
 * so the same control can seed a dataset or replace one. A button rather than a
 * div: it gets keyboard activation and focus treatment for free, which a
 * click-handling div would have to reimplement.
 */
export const DatasetCsvDropzone: React.FC<{
  onSelect: (file: File) => Promise<void>;
  disabled?: boolean;
}> = ({ onSelect, disabled = false }) => {
  const inputRef = React.useRef<HTMLInputElement | null>(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [dragging, setDragging] = useState(false);
  /**
   * A depth count rather than a boolean: dragging across a child element fires
   * `dragleave` on this one, so a boolean drops the highlight mid-drag and
   * flickers for as long as the pointer keeps moving.
   */
  const dragDepth = React.useRef(0);

  const inert = disabled || busy;

  const take = (file: File | undefined): void => {
    if (file === undefined) {
      return;
    }
    if (!isCsvFile(file)) {
      setError(`${file.name} is not a CSV file.`);
      return;
    }
    setError(null);
    setBusy(true);
    void onSelect(file)
      .catch(() => undefined)
      .finally(() => setBusy(false));
  };

  const endDrag = (): void => {
    dragDepth.current = 0;
    setDragging(false);
  };

  return (
    <div className="flex flex-col gap-2">
      <input
        ref={inputRef}
        type="file"
        accept={CSV_ACCEPT}
        className="hidden"
        disabled={inert}
        onChange={(event) => {
          const file = event.target.files?.[0];
          // Cleared so picking the same file twice still fires a change.
          event.target.value = '';
          take(file);
        }}
      />
      <button
        type="button"
        disabled={inert}
        onClick={() => inputRef.current?.click()}
        onDragEnter={(event) => {
          event.preventDefault();
          dragDepth.current += 1;
          setDragging(true);
        }}
        // Both handlers must preventDefault or the browser navigates to the
        // dropped file instead of handing it over.
        onDragOver={(event) => {
          event.preventDefault();
          event.dataTransfer.dropEffect = 'copy';
        }}
        onDragLeave={(event) => {
          event.preventDefault();
          dragDepth.current -= 1;
          if (dragDepth.current <= 0) {
            endDrag();
          }
        }}
        onDrop={(event) => {
          event.preventDefault();
          endDrag();
          take(event.dataTransfer.files[0]);
        }}
        className={cn(
          'flex w-full flex-col items-center justify-center gap-1.5 rounded-md border border-dashed px-4 py-8 text-center transition-colors',
          dragging
            ? 'border-primary bg-primary/5'
            : 'border-input hover:bg-muted/40',
          inert ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'
        )}
      >
        <Icon
          icon="arrow-up-tray"
          size="large"
          className="text-muted-foreground"
        />
        <span className="text-sm font-medium">
          {busy
            ? 'Uploading…'
            : dragging
            ? 'Drop to upload'
            : 'Drop a CSV here, or click to browse'}
        </span>
        <span className="text-xs text-muted-foreground">CSV files only</span>
      </button>
      {error === null ? null : (
        <p className="text-xs text-destructive">{error}</p>
      )}
    </div>
  );
};

/** Name + type for a column the user is declaring. */
export const DatasetAddColumnForm: React.FC<{
  takenNames: ReadonlySet<string>;
  /** A name to start from, when the caller already knows the column it wants. */
  initialName?: string;
  onSubmit: (input: {
    name: string;
    type: DatasetOperationColumnType;
  }) => Promise<void>;
  onCancel: () => void;
}> = ({ takenNames, initialName = '', onSubmit, onCancel }) => {
  const [draft, setDraft] = useState<{
    name: string;
    type: DatasetOperationColumnType;
  }>({ name: initialName, type: 'string' });
  const [busy, setBusy] = useState(false);
  const name = draft.name.trim();
  // Named rather than folded into `canSubmit`: a disabled button with no reason
  // beside it is the whole problem — the user retypes the same name and watches
  // nothing happen.
  const duplicate = name !== '' && takenNames.has(name);
  const canSubmit = !busy && name !== '' && !duplicate;
  const errorId = useId();

  return (
    <form
      // Wraps rather than overflows: the same form is offered inside the
      // workflow node drawer, which is narrower than either toolbar this was
      // written for. A no-op wherever there is room for one line.
      className="flex shrink-0 flex-wrap items-center gap-2 normal-case"
      onSubmit={(event) => {
        event.preventDefault();
        if (!canSubmit) {
          return;
        }
        setBusy(true);
        void onSubmit({ name, type: draft.type })
          .catch(() => undefined)
          .finally(() => setBusy(false));
      }}
    >
      {/* Every control on the row is h-8, the height `size="sm"` gives the
          buttons: an input and a select at their default h-9 beside two h-8
          buttons left the row a pixel short of aligned at both ends. */}
      <Input
        autoFocus
        aria-label="New column name"
        placeholder="Column name"
        className="h-8 w-48"
        aria-invalid={duplicate}
        aria-describedby={duplicate ? errorId : undefined}
        value={draft.name}
        onChange={(event) => setDraft({ ...draft, name: event.target.value })}
      />
      <Select
        value={draft.type}
        onValueChange={(value) =>
          setDraft({ ...draft, type: value as DatasetOperationColumnType })
        }
      >
        <SelectTrigger className="h-8 w-36" aria-label="New column type">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {DATASET_COLUMN_TYPE_OPTIONS.map((option) => (
            <SelectItem key={option.value} value={option.value}>
              {option.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      {/* Add leads, since it is what the row is for and the field beside it was
          just filled in to reach it. Cancel is outlined rather than ghost so the
          way out reads as a control instead of as text. */}
      <Button type="submit" size="sm" disabled={!canSubmit}>
        Add
      </Button>
      <Button type="button" size="sm" variant="outline" onClick={onCancel}>
        Cancel
      </Button>
      {duplicate ? (
        <span id={errorId} role="alert" className="text-xs text-destructive">
          A column named “{name}” already exists
        </span>
      ) : null}
    </form>
  );
};

/**
 * What a dataset shows before it has any columns of its own — the state a newly
 * created dataset starts in. Both ways out are offered directly: declare a
 * column and start typing, or hand it a CSV.
 */
export const DatasetEmptyState: React.FC<{
  canImportCsv: boolean;
  onAddColumn: (input: {
    name: string;
    type: DatasetOperationColumnType;
  }) => Promise<void>;
  onImportCsv?: (file: File) => Promise<void>;
}> = ({ canImportCsv, onAddColumn, onImportCsv }) => {
  const theme = useDatasetTheme();
  const [addingColumn, setAddingColumn] = useState(false);

  return (
    <div
      className={cn(
        'border border-dashed p-8 text-center',
        theme.surfaceRadius,
        theme.surfaceBorder,
        theme.surface
      )}
    >
      <Database className="h-10 w-10 mx-auto text-muted-foreground/50" />
      <Text.Title type="title" className="mt-4">
        This dataset is empty
      </Text.Title>
      <Text.P className="text-muted-foreground mt-2 max-w-xl mx-auto">
        {canImportCsv && onImportCsv !== undefined
          ? 'Import a CSV to fill it in one go, or add a column and start typing.'
          : 'Add a column and start typing.'}
      </Text.P>
      <div className="mt-5 flex items-center justify-center gap-2">
        {addingColumn ? (
          <DatasetAddColumnForm
            takenNames={EMPTY_COLUMN_NAMES}
            onSubmit={async (input) => {
              await onAddColumn(input);
              setAddingColumn(false);
            }}
            onCancel={() => setAddingColumn(false)}
          />
        ) : (
          <>
            {canImportCsv && onImportCsv !== undefined ? (
              <DatasetImportCsvButton
                label="Import CSV"
                variant="outline"
                onImportCsv={onImportCsv}
              />
            ) : null}
            <Button
              type="button"
              size="sm"
              variant="ghost"
              className="h-7"
              onClick={() => setAddingColumn(true)}
            >
              <Plus className="h-3.5 w-3.5" />
              Add column
            </Button>
          </>
        )}
      </div>
    </div>
  );
};

const EMPTY_COLUMN_NAMES: ReadonlySet<string> = new Set<string>();

/**
 * The dataset name, renamed in place. Committing an unsaved dataset's name
 * creates it, so naming is a way to keep a new dataset just like adding a
 * column is.
 */
export const DatasetTitle: React.FC<{
  name: string;
  onRename: (name: string) => Promise<void>;
}> = ({ name, onRename }) => {
  const theme = useDatasetTheme();
  const [draft, setDraft] = useState<string | null>(null);
  const [saving, setSaving] = useState(false);

  const commit = () => {
    const next = draft?.trim() ?? '';
    setDraft(null);
    if (next === '' || next === name) {
      return;
    }
    setSaving(true);
    void onRename(next)
      .catch(() => undefined)
      .finally(() => setSaving(false));
  };

  if (draft === null) {
    return (
      <button
        type="button"
        // Matches the edit input's type so committing does not reflow.
        className={cn(
          'max-w-full truncate rounded px-1 -mx-1 text-left font-title text-2xl leading-8 hover:bg-muted/60 disabled:opacity-60',
          theme.title
        )}
        aria-label="Rename dataset"
        disabled={saving}
        onClick={() => setDraft(name)}
      >
        {name}
      </button>
    );
  }

  return (
    <input
      autoFocus
      aria-label="Dataset name"
      className={cn(
        'w-full rounded border border-input bg-background px-1 -mx-1 font-title text-2xl leading-8 outline-none focus:ring-2 focus:ring-ring',
        theme.title
      )}
      value={draft}
      onChange={(event) => setDraft(event.target.value)}
      onBlur={commit}
      onKeyDown={(event) => {
        if (event.key === 'Enter') {
          event.preventDefault();
          commit();
        }
        if (event.key === 'Escape') {
          event.preventDefault();
          setDraft(null);
        }
      }}
    />
  );
};
